import React, { useContext, useEffect, useState } from "react";

import { authorizeOpenID } from "@mne-authentication/api";
import { SessionContext } from "@mne-authentication/session2";
import { useParams } from "react-router";

import Typography from "@mui/material/Typography";

import { FormattedMessage } from "@mnemonic/intl";
import { PageLayout, RemoveSplash, Section } from "@mnemonic/mui";
import * as ArgusUtils from "@mnemonic/pure-utils/ArgusUtils";
import { extractErrorMessage } from "@mnemonic/redux-utils";

import { LoginBoxCallout } from "../Components/Login/Layout";
import { useSubmitOpenIDAuthCodeByProviderIDMutation } from "../Redux/Service/openIdApi";
import { extractAuthResponse } from "../utils/parseUrl";
import redirectUser from "../utils/redirectUser";
import type { IErrorData } from "./Login/utils/openId";
import {
  createCriteriaForAuthCode,
  decodeAuthState,
  flushOpenIdNonce,
  handleDecodedState,
  persistProviderResponseState,
} from "./Login/utils/openId";
import { redirectWithState } from "./UserAuthorization/utils/redirectWithState";

const REDIRECT_DELAY_MS = 3000;

function OpenIDHandler() {
  const authResponse = extractAuthResponse();

  const [submitOpenIDAuthCodeByProviderID, { isLoading, error, data }] = useSubmitOpenIDAuthCodeByProviderIDMutation();

  const errorMessage = extractErrorMessage(error);
  const params = useParams<{ providerId: string }>();

  const [errorData, setErrorData] = useState<IErrorData>({ error: false, message: null, responseCode: null });

  const apiUrl = ArgusUtils.getApiUrl();
  const { session } = useContext(SessionContext);

  /**
   * Submits auth code and redirects user based on response.
   * Redirect is intentionally delayed for UX readability.
   */
  const submitAuthCode = ({ nextUrl }: { nextUrl: string | null }) => {
    submitOpenIDAuthCodeByProviderID({
      criteria: createCriteriaForAuthCode(params.providerId),
    })
      .unwrap()
      .then(({ responseCode }) => {
        if (responseCode === 201) {
          setTimeout(() => {
            redirectUser({ changePasswordNeeded: false, nextUrl });
          }, REDIRECT_DELAY_MS);
          return;
        }

        if (responseCode === 221) {
          setTimeout(() => {
            redirectUser({ changePasswordNeeded: true, nextUrl });
          }, REDIRECT_DELAY_MS);
          return;
        }

        setErrorData({ error: true, message: `Unexpected response code: ${responseCode}`, responseCode });
      })
      .catch((err) => {
        setErrorData({ error: true, message: String(err), responseCode: 500 });
      });
  };

  const authorize = ({ nextUrl, stateID }: { nextUrl: string; stateID: string }) => {
    authorizeOpenID({ idToken: authResponse.idToken, stateID }, { apiUrl, sessionKey: session.info.sessionKey }).then(
      ({ response, json }) => {
        if (response.status === 200) {
          // We previously only used the "external" nextUrl - unsure why as this is undocumented
          // Spread it after the authorization data response to preserve old behavior
          redirectWithState({ ...json.data, ...{ nextURI: nextUrl ?? json?.data?.nextURI } });
        }
        if ([401, 403, 404, 412].includes(response.status)) {
          setErrorData({
            error: true,
            message: json.messages[0]?.message ?? response.statusText,
            responseCode: response.status,
          });
        }
        if (response.status >= 500) {
          setErrorData({
            error: true,
            message: response.statusText,
            responseCode: response.status,
          });
        }
      },
    );
  };

  useEffect(() => {
    if (!authResponse?.type) return;
    persistProviderResponseState(authResponse.state);
    if (authResponse.type === "error") {
      flushOpenIdNonce();
      setErrorData({ ...errorData, error: true, message: authResponse.errorDescription });
      return;
    }
    try {
      const decodedState = decodeAuthState(authResponse.state);
      handleDecodedState({ decodedState, submitAuthCode, authorize, setErrorData, errorData });
    } catch {
      flushOpenIdNonce();
      submitAuthCode({ nextUrl: null });
    }
  }, [authResponse?.type]);

  return (
    <PageLayout
      PageHeaderProps={{
        content: (
          <Typography variant="h6">
            <FormattedMessage id="login.authentication" defaultMessage="Authentication" />
          </Typography>
        ),
      }}
    >
      <RemoveSplash />
      <Section loading={isLoading} sx={({ spacing }) => ({ maxWidth: spacing(80), minHeight: spacing(20), m: "auto" })}>
        <Section.Content>
          {error || errorData?.error ? (
            <>
              <LoginBoxCallout type="error">
                <FormattedMessage
                  id="login.authentication.openId.failed"
                  defaultMessage="Could not login with OpenId"
                />
              </LoginBoxCallout>

              <Typography variant="h6">
                <FormattedMessage
                  id="login.authentication.openId.failed.description"
                  defaultMessage="Something went wrong logging in with OpenID"
                />
              </Typography>

              <Typography variant="caption">
                {errorMessage || errorData?.message} {data?.data?.timestamp}
              </Typography>
            </>
          ) : (
            <LoginBoxCallout type="info">
              <FormattedMessage
                id="login.authentication.openId.waiting"
                defaultMessage="Verifying credentials and redirecting…"
              />
            </LoginBoxCallout>
          )}
        </Section.Content>
      </Section>
    </PageLayout>
  );
}

export default OpenIDHandler;
